/**
 * ToolRow — single tool entry in the Library sidebar's Tools tab.
 *
 * JSX copied verbatim from designs/tool-discovery-options.html, the
 * `activeTab === 'tools'` branch of the Sidebar list (lines 202-213).
 * Sets `hovered` via onMouseEnter/onMouseLeave so the parent can dock the
 * tool's `long` description in the bottom detail panel.
 */
import type { ToolListItem } from '@shared/schemas/tools';

export interface ToolRowProps {
  t: ToolListItem;
  /** True when the parent's `hoveredTool` slug equals this row's slug. */
  hovered: boolean;
  /** Notify parent of hover state. Called with `t` on enter, `null` on leave. */
  onHover: (t: ToolListItem | null) => void;
}

export function ToolRow({ t, hovered, onHover }: ToolRowProps) {
  return (
    <button
      type="button"
      onMouseEnter={() => onHover(t)}
      onMouseLeave={() => onHover(null)}
      className={`w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 transition-colors ${
        hovered
          ? 'bg-white ring-1 ring-tangerine-300 dark:bg-nightshade-800 dark:ring-tangerine-700'
          : 'hover:bg-white dark:hover:bg-nightshade-800'
      }`}
    >
      <span className="w-7 h-7 flex items-center justify-center bg-nightshade-100 dark:bg-nightshade-800 rounded text-sm">
        {t.icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-nightshade-900 dark:text-nightshade-100 truncate">{t.label}</div>
        <div className="text-[10px] text-nightshade-500 dark:text-nightshade-400 truncate">{t.short}</div>
      </div>
    </button>
  );
}
